import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { T, MONO } from './poyais-ui/theme';
import { Ico } from './poyais-ui/icons';
import { Card, Grid, Button, DeltaPill, Stat } from './poyais-ui/primitives';
import { DateFilterBox } from './poyais-ui/DateFilter';
import { fmt_k, fmt_n, fmt_roas, fmt_pct, pct_dir } from './poyais-ui/format';
import { useLang, addTranslations } from './i18n';
import PoyaisSpinner from './PoyaisSpinner';

addTranslations({
  'Widget Ekle': 'Add Widget',
  'Yükleniyor…': 'Loading…',
  'Henüz widget yok. Eklemek için tıkla.': 'No widgets yet. Click to add one.',
  'Ana Metrikler': 'Main Metrics',
  'Grafikler': 'Charts',
  'Seçenekler': 'Options',
  'Düzenle': 'Edit',
  'Sil': 'Delete',
  'Bağlı değil': 'Not connected',
  'Veri alınamadı.': 'Could not load data.',
  'Birleşik': 'Combined',
  'Harcama': 'Spend',
  'Gösterim': 'Impressions',
  'Tıklama': 'Clicks',
  'Dönüşüm': 'Conversions',
  'Gelir': 'Revenue',
  'TBM': 'CPC',
  'Platform': 'Platform',
  'Kampanya': 'Campaign',
  'Trend için yeterli veri yok.': 'Not enough data for a trend.',
  'Kampanya bulunamadı.': 'No campaigns found.',
  'Veri bulunamadı.': 'No data found.',
  'Kazananlar': 'Winners',
  'Kaybedenler': 'Losers',
  'Artış yok.': 'No growth.',
  'Düşüş yok.': 'No decline.',
  'Gün': 'Day',
  'Hafta': 'Week',
  'Ay': 'Month',
  'Toplam': 'Total',
  'kırılımı': 'breakdown',
  'payı': 'share',
  'Bu Dönem': 'This Period',
  'Önceki Dönem': 'Previous Period',
  'Skor Kartı': 'Scorecard',
  'Tek büyük sayı + değişim': 'One big number + change',
  'KPI Izgarası': 'KPI Grid',
  'Birden çok metrik tek kartta': 'Multiple metrics in one card',
  'Karşılaştırma': 'Comparison',
  'Bu dönem / önceki dönem': 'This period / previous period',
  'Aylık grafik + değişim': 'Monthly chart + change',
  'Çizgi Grafik': 'Line Chart',
  'Gün/Hafta/Ay zaman serisi': 'Day/Week/Month time series',
  'Sütun Grafik': 'Bar Chart',
  'Platform/kampanya kırılımı': 'Platform/campaign breakdown',
  'Yatay Çubuk': 'Horizontal Bar',
  'Pasta Grafik': 'Pie Chart',
  'Pay dağılımı': 'Share distribution',
  'Pay Listesi': 'Share List',
  'Yüzde payı, çubuklu liste': 'Percentage share, bar list',
  'Tablo': 'Table',
  'Kampanya listesi': 'Campaign list',
  'Kazanan / Kaybeden': 'Winner / Loser',
  'En çok artan/azalan kampanyalar': 'Campaigns that grew/declined the most',
  'Widget Düzenle': 'Edit Widget',
  'Bir görsel tipi seç, platform ve metriği belirle.': 'Choose a visual type, and set the platform and metric.',
  'Görselleştirme': 'Visualization',
  'Tüm bağlı platformlar tek metrikte birleştirilir.': 'All connected platforms are combined into a single metric.',
  'Seçilen platformlar yan yana karşılaştırılır.': 'Selected platforms are compared side by side.',
  'Kırılım': 'Breakdown',
  'Metrik': 'Metric',
  'Metrikler': 'Metrics',
  'Kartta gösterilecek metrikleri seç (en az bir tane).': 'Choose the metrics to show on the card (at least one).',
  'Widget Başlığı': 'Widget Title',
  'Örn. Toplam Harcama': 'e.g. Total Spend',
  'Vazgeç': 'Cancel',
  'Kaydet': 'Save',
});

const API = '/api/v1';

type Widget = { id: number; type: string; title: string; platforms: string[]; metrics: string[]; position: number; config: any };
type WidgetDraft = { type: string; title: string; platforms: string[]; metrics: string[]; config: any };

const METRIC_LABELS: Record<string, string> = { spend: 'Harcama', impressions: 'Gösterim', clicks: 'Tıklama', conversions: 'Dönüşüm', revenue: 'Gelir', roas: 'ROAS', ctr: 'CTR', cpc: 'TBM' };
const PLATFORM_LABELS: Record<string, string> = { blend: 'Birleşik', google: 'Google Ads', meta: 'Meta Ads', tiktok: 'TikTok' };
const PLATFORM_ICON: Record<string, string> = { google: '/google-ads-icon.png', meta: '/meta-icon.png', tiktok: '/tiktok-logo.png' };
const PLATFORM_COLOR: Record<string, string> = { blend: T.accent, google: T.gold, meta: T.purple, tiktok: T.blue };
const DIMENSION_LABELS: Record<string, string> = { platform: 'Platform', campaign: 'Kampanya' };
const PIE_PALETTE = [T.gold, T.purple, T.blue, T.greenText, T.accentDeep, T.redText];

const VISUAL_ICON_D: Record<string, string> = {
  scorecard: 'M4 9h16|M4 15h16|M10 3L8 21|M16 3l-2 18',
  kpi_grid: 'M4 4h7v7H4z|M13 4h7v7h-7z|M4 13h7v7H4z|M13 13h7v7h-7z',
  trend: 'M3 3v18h18|M7 15l4-4 3 3 5-6',
  line: 'M3 3v18h18|M7 15l4-4 3 3 5-6',
  bar: 'M4 20V10|M10 20V4|M16 20v14|M4 20h16',
  horizontal_bar: 'M4 6h10|M4 12h16|M4 18h7',
  pie: 'M12 3a9 9 0 100 18 9 9 0 000-18z|M12 3v9h9',
  delta: 'M7 4v11|M4 7l3-3 3 3|M17 20V9|M14 17l3 3 3-3',
  table: 'M3 5h18v14H3z|M3 10h18|M9 5v14',
  winners_losers: 'M8 4h8v4a4 4 0 01-8 0z|M8 6H5a3 3 0 003 3|M16 6h3a3 3 0 01-3 3|M12 12v4|M9 20h6|M10 16h4v4h-4z',
  share: 'M17 7L7 17|M8.5 9a1.5 1.5 0 100-3 1.5 1.5 0 000 3z|M15.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z',
};

const WIDGET_TYPES: { id: string; label: string; desc: string; group: 'metric' | 'chart'; needsMetric: boolean; needsDimension: boolean; multiMetric?: boolean }[] = [
  { id: 'scorecard', label: 'Skor Kartı', desc: 'Tek büyük sayı + değişim', group: 'metric', needsMetric: true, needsDimension: false },
  { id: 'kpi_grid', label: 'KPI Izgarası', desc: 'Birden çok metrik tek kartta', group: 'metric', needsMetric: false, needsDimension: false, multiMetric: true },
  { id: 'delta', label: 'Karşılaştırma', desc: 'Bu dönem / önceki dönem', group: 'metric', needsMetric: true, needsDimension: false },
  { id: 'trend', label: 'Trend', desc: 'Aylık grafik + değişim', group: 'metric', needsMetric: true, needsDimension: false },
  { id: 'line', label: 'Çizgi Grafik', desc: 'Gün/Hafta/Ay zaman serisi', group: 'chart', needsMetric: true, needsDimension: false },
  { id: 'bar', label: 'Sütun Grafik', desc: 'Platform/kampanya kırılımı', group: 'chart', needsMetric: true, needsDimension: true },
  { id: 'horizontal_bar', label: 'Yatay Çubuk', desc: 'Platform/kampanya kırılımı', group: 'chart', needsMetric: true, needsDimension: true },
  { id: 'pie', label: 'Pasta Grafik', desc: 'Pay dağılımı', group: 'chart', needsMetric: true, needsDimension: true },
  { id: 'share', label: 'Pay Listesi', desc: 'Yüzde payı, çubuklu liste', group: 'chart', needsMetric: true, needsDimension: true },
  { id: 'table', label: 'Tablo', desc: 'Kampanya listesi', group: 'chart', needsMetric: true, needsDimension: false },
  { id: 'winners_losers', label: 'Kazanan / Kaybeden', desc: 'En çok artan/azalan kampanyalar', group: 'chart', needsMetric: false, needsDimension: false },
];
const WIDGET_TYPE_MAP = Object.fromEntries(WIDGET_TYPES.map(w => [w.id, w]));

function fmtMetric(key: string, v: number) {
  if (key === 'spend' || key === 'revenue') return fmt_k(v);
  if (key === 'roas') return fmt_roas(v);
  if (key === 'ctr') return `%${(v || 0).toFixed(2)}`;
  if (key === 'cpc') return fmt_k(v);
  return fmt_n(v);
}

function widgetDataUrl(clientId: number, w: Pick<Widget, 'type' | 'platforms' | 'metrics' | 'config'>, start: string, end: string) {
  const qs = new URLSearchParams();
  qs.set('client_id', String(clientId));
  qs.set('type', w.type);
  qs.set('start_date', start);
  qs.set('end_date', end);
  (w.platforms || ['blend']).forEach(p => qs.append('platforms', p));
  (w.metrics || ['spend']).forEach(m => qs.append('metrics', m));
  if (w.config?.dimension) qs.set('dimension', w.config.dimension);
  return `${API}/dashboard/widget-data?${qs.toString()}`;
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 11, fontWeight: 700, color: T.textDim, textTransform: 'uppercase' as const, letterSpacing: '.08em', marginBottom: -6 }}>{children}</div>;
}

export default function PanoDetail({ boardId, boardTitle, clientId, onBack }: { boardId: number; boardTitle: string; clientId: number; onBack: () => void }) {
  const { t } = useLang();
  const [widgets, setWidgets] = useState<Widget[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalWidget, setModalWidget] = useState<Widget | null | 'new'>(null);
  const [start, setStart] = useState(() => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); });
  const [end, setEnd] = useState(() => new Date().toISOString().slice(0, 10));
  const [dragId, setDragId] = useState<number | null>(null);

  const load = () => {
    axios.get(`${API}/dashboard/widgets`, { params: { client_id: clientId, board_id: boardId } })
      .then(r => setWidgets(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [boardId, clientId]);

  const addWidget = (w: WidgetDraft) => {
    axios.post(`${API}/dashboard/widgets`, { client_id: clientId, board_id: boardId, ...w })
      .then(r => setWidgets(ws => [...ws, r.data])).catch(() => {});
  };
  const saveEdit = (id: number, w: WidgetDraft) => {
    axios.put(`${API}/dashboard/widgets/${id}`, { client_id: clientId, board_id: boardId, ...w })
      .then(r => setWidgets(ws => ws.map(x => x.id === id ? r.data : x))).catch(() => {});
  };
  const removeWidget = (id: number) => {
    axios.delete(`${API}/dashboard/widgets/${id}`, { params: { client_id: clientId } })
      .then(() => setWidgets(ws => ws.filter(w => w.id !== id))).catch(() => {});
  };
  const reorder = (newOrder: Widget[]) => {
    setWidgets(newOrder);
    axios.post(`${API}/dashboard/widgets/reorder`, { client_id: clientId, order: newOrder.map(w => w.id) }).catch(() => {});
  };
  const onDrop = (targetId: number) => {
    if (dragId === null || dragId === targetId) return;
    const arr = [...widgets];
    const from = arr.findIndex(w => w.id === dragId), to = arr.findIndex(w => w.id === targetId);
    if (from === -1 || to === -1) return;
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    setDragId(null);
    reorder(arr);
  };

  const metricWidgets = widgets.filter(w => WIDGET_TYPE_MAP[w.type]?.group === 'metric');
  const chartWidgets = widgets.filter(w => WIDGET_TYPE_MAP[w.type]?.group !== 'metric');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' as const, gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={onBack} style={{ width: 32, height: 32, borderRadius: 9, border: `1px solid ${T.border}`, background: 'var(--py-surface)', cursor: 'pointer', color: T.textMid, fontSize: 15, flexShrink: 0 }}>←</button>
          <div style={{ fontSize: 19, fontWeight: 800, letterSpacing: '-0.02em', color: T.text }}>{boardTitle}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <DateFilterBox start={start} end={end} onChange={(s, e) => { setStart(s); setEnd(e); }} />
          <Button variant="solid" onClick={() => setModalWidget('new')}>{t('Widget Ekle')}</Button>
        </div>
      </div>

      {loading ? (
        <PoyaisSpinner size={36} pad={24}/>
      ) : widgets.length === 0 ? (
        <div onClick={() => setModalWidget('new')} style={{ border: '1px dashed var(--py-border-soft)', borderRadius: 16, padding: '54px 22px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: T.textMid }}>{t('Henüz widget yok. Eklemek için tıkla.')}</span>
        </div>
      ) : (
        <>
          {metricWidgets.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <SectionLabel>{t('Ana Metrikler')}</SectionLabel>
              <Grid min={280} gap={14}>
                {metricWidgets.map(w => (
                  <div key={w.id} draggable onDragStart={() => setDragId(w.id)} onDragOver={e => e.preventDefault()} onDrop={() => onDrop(w.id)} style={{ cursor: 'grab' }}>
                    <WidgetCard widget={w} clientId={clientId} start={start} end={end} onDelete={() => removeWidget(w.id)} onEdit={() => setModalWidget(w)} />
                  </div>
                ))}
              </Grid>
            </div>
          )}
          {chartWidgets.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <SectionLabel>{t('Grafikler')}</SectionLabel>
              <Grid min={440} gap={14}>
                {chartWidgets.map(w => (
                  <div key={w.id} draggable onDragStart={() => setDragId(w.id)} onDragOver={e => e.preventDefault()} onDrop={() => onDrop(w.id)} style={{ cursor: 'grab' }}>
                    <WidgetCard widget={w} clientId={clientId} start={start} end={end} onDelete={() => removeWidget(w.id)} onEdit={() => setModalWidget(w)} />
                  </div>
                ))}
              </Grid>
            </div>
          )}
        </>
      )}

      {modalWidget && (
        <WidgetModal
          initial={modalWidget === 'new' ? null : modalWidget}
          onClose={() => setModalWidget(null)}
          onSave={(w) => { modalWidget === 'new' ? addWidget(w) : saveEdit((modalWidget as Widget).id, w); setModalWidget(null); }}
        />
      )}
    </div>
  );
}

function WidgetMenu({ onEdit, onDelete }: { onEdit: () => void; onDelete: () => void }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: 'absolute', top: 10, right: 10 }}>
      <button onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        style={{ width: 24, height: 24, borderRadius: 7, border: 'none', background: open ? T.border : 'transparent', color: 'var(--py-text-dim)', cursor: 'pointer', fontSize: 15, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        title={t('Seçenekler')}>⋯</button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 10 }} />
          <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', top: 28, right: 0, background: 'var(--py-surface)', border: `1px solid ${T.border}`, borderRadius: 11, boxShadow: '0 4px 18px rgba(20,22,43,.12)', zIndex: 11, minWidth: 128, overflow: 'hidden' }}>
            <button onClick={() => { setOpen(false); onEdit(); }} style={{ display: 'block', width: '100%', textAlign: 'left' as const, padding: '9px 13px', border: 'none', background: 'none', fontSize: 12.5, fontWeight: 600, color: T.text, cursor: 'pointer' }}>{t('Düzenle')}</button>
            <button onClick={() => { setOpen(false); onDelete(); }} style={{ display: 'block', width: '100%', textAlign: 'left' as const, padding: '9px 13px', border: 'none', background: 'none', fontSize: 12.5, fontWeight: 600, color: T.redText, cursor: 'pointer' }}>{t('Sil')}</button>
          </div>
        </>
      )}
    </div>
  );
}

function WidgetCard({ widget, clientId, start, end, onDelete, onEdit }: { widget: Widget; clientId: number; start: string; end: string; onDelete: () => void; onEdit: () => void }) {
  const { t } = useLang();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(widgetDataUrl(clientId, widget, start, end)).then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [widget.id, widget.type, JSON.stringify(widget.platforms), JSON.stringify(widget.metrics), JSON.stringify(widget.config), clientId, start, end]);

  const metric = widget.metrics?.[0] || 'spend';
  const isBlend = !widget.platforms || widget.platforms.includes('blend');

  return (
    <Card pad={18} style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 12, minHeight: 150 }}>
      <WidgetMenu onEdit={onEdit} onDelete={onDelete} />

      <div>
        <div style={{ fontSize: 13.5, fontWeight: 800, color: T.text, letterSpacing: '-0.01em', marginBottom: 4, paddingRight: 26 }}>{widget.title}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10.5, color: T.textDim, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '.05em' }}>
          {isBlend ? <span>{t('Birleşik')}</span> : widget.platforms.map(p => (
            <span key={p} style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><img src={PLATFORM_ICON[p]} width={11} height={11} style={{ objectFit: 'contain' }} />{PLATFORM_LABELS[p]}</span>
          ))}
        </div>
      </div>

      {loading ? (
        <PoyaisSpinner size={26} pad={10}/>
      ) : !data ? (
        <div style={{ fontSize: 12, color: T.textDim }}>{t('Veri alınamadı.')}</div>
      ) : widget.type === 'scorecard' ? (
        <ScorecardBody data={data} metric={metric} isBlend={isBlend} />
      ) : widget.type === 'kpi_grid' ? (
        <KpiGridBody data={data} metrics={widget.metrics?.length ? widget.metrics : ['spend']} isBlend={isBlend} />
      ) : widget.type === 'delta' ? (
        <DeltaBody data={data} metric={metric} isBlend={isBlend} />
      ) : widget.type === 'trend' ? (
        <TrendBody data={data} metric={metric} isBlend={isBlend} />
      ) : widget.type === 'table' ? (
        <TableBody data={data} metric={metric} />
      ) : widget.type === 'winners_losers' ? (
        <WinnersLosersBody data={data} />
      ) : widget.type === 'line' ? (
        <LineBody data={data} metric={metric} />
      ) : widget.type === 'bar' ? (
        <BarBody data={data} metric={metric} />
      ) : widget.type === 'horizontal_bar' ? (
        <BarBody data={data} metric={metric} horizontal />
      ) : widget.type === 'pie' ? (
        <PieBody data={data} metric={metric} />
      ) : widget.type === 'share' ? (
        <ShareBody data={data} metric={metric} />
      ) : null}
    </Card>
  );
}

function PlatformRows({ series, metric }: { series: Record<string, any>; metric: string }) {
  const { t } = useLang();
  return (
    <div>
      {Object.entries(series).map(([p, s]: [string, any]) => {
        if (s.connected === false) {
          return (
            <div key={p} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderTop: `1px solid ${T.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><img src={PLATFORM_ICON[p]} width={14} height={14} style={{ objectFit: 'contain' }} /><span style={{ fontSize: 12, color: T.textMid }}>{PLATFORM_LABELS[p]}</span></div>
              <span style={{ fontSize: 11, color: T.textFaint }}>{t('Bağlı değil')}</span>
            </div>
          );
        }
        const cur = s.current?.[metric] || 0, prev = s.prev?.[metric] || 0;
        return (
          <div key={p} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderTop: `1px solid ${T.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><img src={PLATFORM_ICON[p]} width={14} height={14} style={{ objectFit: 'contain' }} /><span style={{ fontSize: 12, color: T.textMid }}>{PLATFORM_LABELS[p]}</span></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontFamily: MONO, fontWeight: 800, fontSize: 13, color: T.text }}>{fmtMetric(metric, cur)}</span>
              <DeltaPill value={fmt_pct(cur, prev)} dir={pct_dir(cur, prev)} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ScorecardBody({ data, metric, isBlend }: { data: any; metric: string; isBlend: boolean }) {
  const { t } = useLang();
  if (isBlend) {
    const s = data.series?.blend;
    const cur = s?.current?.[metric] || 0, prev = s?.prev?.[metric] || 0;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Stat label={t(METRIC_LABELS[metric])} value={fmtMetric(metric, cur)} size={26} />
        <DeltaPill value={fmt_pct(cur, prev)} dir={pct_dir(cur, prev)} />
      </div>
    );
  }
  return <PlatformRows series={data.series || {}} metric={metric} />;
}

function combinedSpark(sparkline: any, metric: string): { month: string; v: number }[] {
  const keys = Object.keys(sparkline || {});
  if (keys.length === 0) return [];
  const base = sparkline[keys[0]];
  return (base || []).map((row: any, i: number) => {
    let v = 0;
    keys.forEach(k => { const r = sparkline[k]?.[i]; if (r) v += (r[metric] || 0); });
    return { month: row.month, v };
  });
}

function SparkTooltip({ metric }: { metric: string }) {
  const { t } = useLang();
  return (
    <Tooltip
      cursor={false}
      formatter={(v: any) => [fmtMetric(metric, Number(v)), METRIC_LABELS[metric] ? t(METRIC_LABELS[metric]) : metric]}
      contentStyle={{ background: '#1A1A2E', border: 'none', borderRadius: 8, padding: '5px 9px', fontSize: 11 }}
      labelStyle={{ display: 'none' }}
      itemStyle={{ color: '#fff', padding: 0 }}
    />
  );
}

function TrendBody({ data, metric, isBlend }: { data: any; metric: string; isBlend: boolean }) {
  const { t } = useLang();
  const spark = combinedSpark(data.sparkline, metric);
  const chart = spark.length > 1 ? (
    <div style={{ height: 52 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={spark}>
          <Line type="monotone" dataKey="v" stroke={T.accent} strokeWidth={2} dot={false} />
          <SparkTooltip metric={metric} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  ) : <div style={{ fontSize: 11, color: T.textFaint }}>{t('Trend için yeterli veri yok.')}</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {isBlend ? <ScorecardBody data={data} metric={metric} isBlend={true} /> : <PlatformRows series={data.series || {}} metric={metric} />}
      {chart}
    </div>
  );
}

function TableBody({ data, metric }: { data: any; metric: string }) {
  const { t } = useLang();
  const rows = data.rows || [];
  if (rows.length === 0) return <div style={{ fontSize: 12, color: T.textDim }}>{t('Kampanya bulunamadı.')}</div>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, maxHeight: 220, overflowY: 'auto' as const }}>
      {rows.map((r: any, i: number) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '7px 0', borderTop: i ? `1px solid ${T.border}` : 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            <img src={PLATFORM_ICON[r.platform]} width={12} height={12} style={{ objectFit: 'contain', flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: T.text, whiteSpace: 'nowrap' as const, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <span style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, color: T.text }}>{fmtMetric(metric, r[metric] || 0)}</span>
            <DeltaPill value={`${r.delta_pct >= 0 ? '+' : ''}${r.delta_pct}%`} dir={r.delta_pct > 0 ? 'up' : r.delta_pct < 0 ? 'down' : 'flat'} />
          </div>
        </div>
      ))}
    </div>
  );
}

function WinnersLosersBody({ data }: { data: any }) {
  const { t } = useLang();
  const winners = data.winners || [], losers = data.losers || [];
  const Row = ({ r }: { r: any }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '6px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
        <img src={PLATFORM_ICON[r.platform]} width={12} height={12} style={{ objectFit: 'contain', flexShrink: 0 }} />
        <span style={{ fontSize: 12, color: T.text, whiteSpace: 'nowrap' as const, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</span>
      </div>
      <DeltaPill value={`${r.delta_pct >= 0 ? '+' : ''}${r.delta_pct}%`} dir={r.delta_pct > 0 ? 'up' : 'down'} />
    </div>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <div style={{ fontSize: 10.5, fontWeight: 700, color: T.greenText, textTransform: 'uppercase' as const, letterSpacing: '.05em', marginBottom: 4 }}>{t('Kazananlar')}</div>
        {winners.length === 0 ? <div style={{ fontSize: 11, color: T.textFaint }}>{t('Artış yok.')}</div> : winners.map((r: any, i: number) => <Row key={i} r={r} />)}
      </div>
      <div>
        <div style={{ fontSize: 10.5, fontWeight: 700, color: T.redText, textTransform: 'uppercase' as const, letterSpacing: '.05em', marginBottom: 4 }}>{t('Kaybedenler')}</div>
        {losers.length === 0 ? <div style={{ fontSize: 11, color: T.textFaint }}>{t('Düşüş yok.')}</div> : losers.map((r: any, i: number) => <Row key={i} r={r} />)}
      </div>
    </div>
  );
}

// ── Çizgi Grafik: gün bazlı seri, Gün/Hafta/Ay yeniden örnekleme istemci tarafında ──
function bucketKey(dateStr: string, gran: 'day' | 'week' | 'month') {
  const d = new Date(dateStr + 'T00:00:00');
  if (gran === 'month') return dateStr.slice(0, 7);
  if (gran === 'week') {
    const day = (d.getDay() + 6) % 7; // Pazartesi=0
    d.setDate(d.getDate() - day);
    return d.toISOString().slice(0, 10);
  }
  return dateStr;
}
function resample(points: { date: string; value: number }[], gran: 'day' | 'week' | 'month') {
  if (gran === 'day') return points;
  const buckets: Record<string, number> = {};
  points.forEach(p => { const k = bucketKey(p.date, gran); buckets[k] = (buckets[k] || 0) + p.value; });
  return Object.entries(buckets).sort(([a], [b]) => a.localeCompare(b)).map(([date, value]) => ({ date, value }));
}
function fmtDateLabel(dateStr: string, gran: 'day' | 'week' | 'month') {
  if (gran === 'month') { const [y, m] = dateStr.split('-'); return `${m}.${y.slice(2)}`; }
  const d = new Date(dateStr + 'T00:00:00');
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function GranularityToggle({ value, onChange }: { value: 'day' | 'week' | 'month'; onChange: (g: 'day' | 'week' | 'month') => void }) {
  const { t } = useLang();
  const opts: { id: 'day' | 'week' | 'month'; label: string }[] = [{ id: 'day', label: 'Gün' }, { id: 'week', label: 'Hafta' }, { id: 'month', label: 'Ay' }];
  return (
    <div style={{ display: 'flex', background: 'var(--py-track-bg)', borderRadius: 9, padding: 2, gap: 2 }}>
      {opts.map(o => (
        <button key={o.id} onClick={() => onChange(o.id)} style={{
          padding: '4px 9px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700,
          background: value === o.id ? 'var(--py-surface)' : 'transparent', color: value === o.id ? T.text : T.textDim,
          boxShadow: value === o.id ? '0 1px 2px rgba(20,22,43,.1)' : 'none',
        }}>{t(o.label)}</button>
      ))}
    </div>
  );
}

function LineBody({ data, metric }: { data: any; metric: string }) {
  const { t } = useLang();
  const [gran, setGran] = useState<'day' | 'week' | 'month'>('day');
  const series: Record<string, { date: string; value: number }[]> = data.series || {};
  const keys = Object.keys(series);
  if (keys.length === 0 || (series[keys[0]] || []).length === 0) {
    return <div style={{ fontSize: 12, color: T.textDim }}>{t('Veri bulunamadı.')}</div>;
  }

  const resampled: Record<string, { date: string; value: number }[]> = {};
  keys.forEach(k => { resampled[k] = resample(series[k], gran); });
  const dates = Array.from(new Set(keys.flatMap(k => resampled[k].map(p => p.date)))).sort();
  const merged = dates.map(date => {
    const row: any = { date, label: fmtDateLabel(date, gran) };
    keys.forEach(k => { row[k] = resampled[k].find(p => p.date === date)?.value || 0; });
    return row;
  });
  const total = keys.reduce((sum, k) => sum + resampled[k].reduce((s, p) => s + p.value, 0), 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <GranularityToggle value={gran} onChange={setGran} />
      </div>
      <div style={{ height: 220 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={merged} margin={{ top: 6, right: 6, left: -18, bottom: 0 }}>
            <CartesianGrid strokeDasharray="2 6" stroke="var(--py-divider)" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: T.textDim, fontFamily: MONO }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: T.textDim, fontFamily: MONO }} axisLine={false} tickLine={false} tickFormatter={(v) => fmtMetric(metric, v)} width={54} />
            <Tooltip
              formatter={(v: any, name: any) => [fmtMetric(metric, Number(v)), PLATFORM_LABELS[name as string] || name]}
              labelFormatter={(l) => l}
              contentStyle={{ borderRadius: 10, border: `1px solid ${T.border}`, fontSize: 12 }}
            />
            {keys.map(k => (
              <Line key={k} type="monotone" dataKey={k} name={k} stroke={PLATFORM_COLOR[k] || T.accent} strokeWidth={2.2} dot={{ r: 2.5 }} activeDot={{ r: 4 }} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 8, borderTop: `1px solid ${T.border}`, fontSize: 11.5 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          {keys.map(k => (
            <span key={k} style={{ display: 'flex', alignItems: 'center', gap: 5, color: T.textMid }}>
              <span style={{ width: 8, height: 8, borderRadius: 3, background: PLATFORM_COLOR[k] || T.accent }} />
              {PLATFORM_LABELS[k] || k}
            </span>
          ))}
        </div>
        <span style={{ color: T.textDim }}>{t('Toplam')} <b style={{ color: T.text, fontFamily: MONO }}>{fmtMetric(metric, total)}</b></span>
      </div>
    </div>
  );
}

function BarBody({ data, metric, horizontal }: { data: any; metric: string; horizontal?: boolean }) {
  const { t } = useLang();
  const rows = data.rows || [];
  if (rows.length === 0) return <div style={{ fontSize: 12, color: T.textDim }}>{t('Veri bulunamadı.')}</div>;
  const total = rows.reduce((s: number, r: any) => s + (r.value || 0), 0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontSize: 11, color: T.textDim, fontWeight: 600 }}>{t(DIMENSION_LABELS[data.dimension] || data.dimension)} {t('kırılımı')}</div>
      <div style={{ height: horizontal ? Math.max(140, rows.length * 34) : 220 }}>
        <ResponsiveContainer width="100%" height="100%">
          {horizontal ? (
            <BarChart data={rows} layout="vertical" margin={{ top: 6, right: 14, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="2 6" stroke="var(--py-divider)" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10, fill: T.textDim, fontFamily: MONO }} axisLine={false} tickLine={false} tickFormatter={(v) => fmtMetric(metric, v)} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 10.5, fill: T.textMid }} axisLine={false} tickLine={false} width={100} />
              <Tooltip formatter={(v: any) => [fmtMetric(metric, Number(v)), METRIC_LABELS[metric] ? t(METRIC_LABELS[metric]) : metric]} contentStyle={{ borderRadius: 10, border: `1px solid ${T.border}`, fontSize: 12 }} />
              <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={16}>
                {rows.map((r: any, i: number) => <Cell key={i} fill={PLATFORM_COLOR[r.platform] || PIE_PALETTE[i % PIE_PALETTE.length]} />)}
              </Bar>
            </BarChart>
          ) : (
            <BarChart data={rows} margin={{ top: 6, right: 6, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="2 6" stroke="var(--py-divider)" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: T.textDim, fontFamily: MONO }} axisLine={false} tickLine={false} interval={0} angle={rows.length > 5 ? -20 : 0} textAnchor={rows.length > 5 ? 'end' : 'middle'} height={rows.length > 5 ? 38 : 22} />
              <YAxis tick={{ fontSize: 10, fill: T.textDim, fontFamily: MONO }} axisLine={false} tickLine={false} tickFormatter={(v) => fmtMetric(metric, v)} width={54} />
              <Tooltip formatter={(v: any) => [fmtMetric(metric, Number(v)), METRIC_LABELS[metric] ? t(METRIC_LABELS[metric]) : metric]} contentStyle={{ borderRadius: 10, border: `1px solid ${T.border}`, fontSize: 12 }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {rows.map((r: any, i: number) => <Cell key={i} fill={PLATFORM_COLOR[r.platform] || PIE_PALETTE[i % PIE_PALETTE.length]} />)}
              </Bar>
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
      <div style={{ textAlign: 'right' as const, fontSize: 11.5, color: T.textDim, paddingTop: 8, borderTop: `1px solid ${T.border}` }}>
        {t('Toplam')} <b style={{ color: T.text, fontFamily: MONO }}>{fmtMetric(metric, total)}</b>
      </div>
    </div>
  );
}

function KpiGridBody({ data, metrics, isBlend }: { data: any; metrics: string[]; isBlend: boolean }) {
  const { t } = useLang();
  const s = isBlend ? data.series?.blend : data.series?.[Object.keys(data.series || {})[0]];
  if (!s) return <div style={{ fontSize: 12, color: T.textDim }}>{t('Veri bulunamadı.')}</div>;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 12 }}>
      {metrics.map(m => {
        const cur = s.current?.[m] || 0, prev = s.prev?.[m] || 0;
        return (
          <div key={m} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <div style={{ fontSize: 10.5, color: T.textDim, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '.04em' }}>{METRIC_LABELS[m] ? t(METRIC_LABELS[m]) : m}</div>
            <div style={{ fontFamily: MONO, fontWeight: 800, fontSize: 17, color: T.text }}>{fmtMetric(m, cur)}</div>
            <DeltaPill value={fmt_pct(cur, prev)} dir={pct_dir(cur, prev)} />
          </div>
        );
      })}
    </div>
  );
}

function DeltaBody({ data, metric, isBlend }: { data: any; metric: string; isBlend: boolean }) {
  const { t } = useLang();
  const s = isBlend ? data.series?.blend : data.series?.[Object.keys(data.series || {})[0]];
  if (!s) return <div style={{ fontSize: 12, color: T.textDim }}>{t('Veri bulunamadı.')}</div>;
  const cur = s.current?.[metric] || 0, prev = s.prev?.[metric] || 0;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14 }}>
        <div>
          <div style={{ fontSize: 10.5, color: T.textDim, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '.04em', marginBottom: 4 }}>{t('Bu Dönem')}</div>
          <div style={{ fontFamily: MONO, fontWeight: 800, fontSize: 24, color: T.text }}>{fmtMetric(metric, cur)}</div>
        </div>
        <div style={{ paddingBottom: 3 }}>
          <div style={{ fontSize: 10.5, color: T.textFaint, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '.04em', marginBottom: 4 }}>{t('Önceki Dönem')}</div>
          <div style={{ fontFamily: MONO, fontWeight: 700, fontSize: 15, color: T.textFaint }}>{fmtMetric(metric, prev)}</div>
        </div>
      </div>
      <DeltaPill value={fmt_pct(cur, prev)} dir={pct_dir(cur, prev)} />
    </div>
  );
}

function ShareBody({ data, metric }: { data: any; metric: string }) {
  const { t } = useLang();
  const rows = (data.rows || []).filter((r: any) => r.value > 0);
  if (rows.length === 0) return <div style={{ fontSize: 12, color: T.textDim }}>{t('Veri bulunamadı.')}</div>;
  const total = rows.reduce((s: number, r: any) => s + r.value, 0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 11, color: T.textDim, fontWeight: 600 }}>{t(DIMENSION_LABELS[data.dimension] || data.dimension)} {t('payı')}</div>
      {rows.map((r: any, i: number) => {
        const pct = total ? r.value / total * 100 : 0;
        const color = PLATFORM_COLOR[r.platform] || PIE_PALETTE[i % PIE_PALETTE.length];
        return (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12 }}>
              <span style={{ color: T.textMid, whiteSpace: 'nowrap' as const, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                <span style={{ fontFamily: MONO, fontWeight: 700, color: T.text }}>{fmtMetric(metric, r.value)}</span>
                <span style={{ color: T.textFaint, fontSize: 11, minWidth: 32, textAlign: 'right' as const }}>%{pct.toFixed(1)}</span>
              </span>
            </div>
            <div style={{ height: 6, borderRadius: 4, background: 'var(--py-table-divider)', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 4 }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PieBody({ data, metric }: { data: any; metric: string }) {
  const { t } = useLang();
  const rows = (data.rows || []).filter((r: any) => r.value > 0);
  if (rows.length === 0) return <div style={{ fontSize: 12, color: T.textDim }}>{t('Veri bulunamadı.')}</div>;
  const total = rows.reduce((s: number, r: any) => s + r.value, 0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontSize: 11, color: T.textDim, fontWeight: 600 }}>{t(DIMENSION_LABELS[data.dimension] || data.dimension)} {t('payı')}</div>
      <div style={{ height: 190 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={rows} dataKey="value" nameKey="name" innerRadius={48} outerRadius={78} paddingAngle={2}>
              {rows.map((r: any, i: number) => <Cell key={i} fill={PLATFORM_COLOR[r.platform] || PIE_PALETTE[i % PIE_PALETTE.length]} />)}
            </Pie>
            <Tooltip formatter={(v: any, n: any) => [fmtMetric(metric, Number(v)), n]} contentStyle={{ borderRadius: 10, border: `1px solid ${T.border}`, fontSize: 12 }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {rows.map((r: any, i: number) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: T.textMid, minWidth: 0 }}>
              <span style={{ width: 8, height: 8, borderRadius: 3, background: PLATFORM_COLOR[r.platform] || PIE_PALETTE[i % PIE_PALETTE.length], flexShrink: 0 }} />
              <span style={{ whiteSpace: 'nowrap' as const, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</span>
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              <span style={{ fontFamily: MONO, fontWeight: 700, color: T.text }}>{fmtMetric(metric, r.value)}</span>
              <span style={{ color: T.textFaint, fontSize: 11 }}>{total ? Math.round(r.value / total * 100) : 0}%</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function WidgetModal({ initial, onClose, onSave }: { initial: Widget | null; onClose: () => void; onSave: (w: WidgetDraft) => void }) {
  const { t } = useLang();
  const [type, setType] = useState(initial?.type || 'scorecard');
  const [title, setTitle] = useState(initial?.title || '');
  const [platforms, setPlatforms] = useState<string[]>(initial?.platforms?.length ? initial.platforms : ['blend']);
  const [metric, setMetric] = useState(initial?.metrics?.[0] || 'spend');
  const [metricsMulti, setMetricsMulti] = useState<string[]>(initial?.metrics?.length ? initial.metrics : ['spend', 'roas']);
  const [dimension, setDimension] = useState(initial?.config?.dimension || 'platform');

  const meta = WIDGET_TYPE_MAP[type];
  const toggleMetricMulti = (m: string) => {
    setMetricsMulti(ms => ms.includes(m) ? (ms.length > 1 ? ms.filter(x => x !== m) : ms) : [...ms, m]);
  };

  const togglePlatform = (p: string) => {
    if (p === 'blend') { setPlatforms(['blend']); return; }
    setPlatforms(ps => {
      const withoutBlend = ps.filter(x => x !== 'blend');
      return withoutBlend.includes(p) ? withoutBlend.filter(x => x !== p) : [...withoutBlend, p];
    });
  };

  const save = () => {
    if (!title.trim()) return;
    onSave({
      type, title: title.trim(), platforms: platforms.length ? platforms : ['blend'],
      metrics: meta.multiMetric ? metricsMulti : [metric], config: meta.needsDimension ? { dimension } : {},
    });
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(20,22,43,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--py-surface)', borderRadius: 18, padding: 26, width: 520, maxWidth: '100%', maxHeight: '86vh', overflowY: 'auto' as const, display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: T.text }}>{initial ? t('Widget Düzenle') : t('Widget Ekle')}</div>
          <div style={{ fontSize: 12, color: T.textDim, marginTop: 3 }}>{t('Bir görsel tipi seç, platform ve metriği belirle.')}</div>
        </div>

        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.textDim, marginBottom: 8, textTransform: 'uppercase' as const, letterSpacing: '.06em' }}>{t('Görselleştirme')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(102px, 1fr))', gap: 8 }}>
            {WIDGET_TYPES.map(wt => (
              <div key={wt.id} onClick={() => setType(wt.id)} style={{
                border: `1.5px solid ${type === wt.id ? T.accent : T.border}`, background: type === wt.id ? 'rgba(201,168,76,.08)' : 'var(--py-surface)',
                borderRadius: 12, padding: '12px 10px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7, textAlign: 'center' as const,
              }}>
                <Ico d={VISUAL_ICON_D[wt.id]} size={18} sw={2} color={type === wt.id ? T.accentDeep : T.textMid} />
                <div style={{ fontSize: 11, fontWeight: 700, color: type === wt.id ? T.accentDeep : T.text, lineHeight: 1.25 }}>{t(wt.label)}</div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 11, color: T.textFaint, marginTop: 8 }}>{t(meta.desc)}</div>
        </div>

        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.textDim, marginBottom: 8, textTransform: 'uppercase' as const, letterSpacing: '.06em' }}>{t('Platform')}</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const }}>
            {['blend', 'google', 'meta', 'tiktok'].map(p => {
              const active = platforms.includes(p);
              return (
                <button key={p} onClick={() => togglePlatform(p)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 999, border: `1.5px solid ${active ? T.accent : T.border}`, background: active ? 'rgba(201,168,76,.1)' : 'var(--py-surface)', color: active ? T.accentDeep : T.textMid, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  {p !== 'blend' && <img src={PLATFORM_ICON[p]} width={14} height={14} style={{ objectFit: 'contain' }} />}
                  {p === 'blend' ? t(PLATFORM_LABELS[p]) : PLATFORM_LABELS[p]}
                </button>
              );
            })}
          </div>
          <div style={{ fontSize: 11, color: T.textFaint, marginTop: 6 }}>{platforms.includes('blend') ? t('Tüm bağlı platformlar tek metrikte birleştirilir.') : t('Seçilen platformlar yan yana karşılaştırılır.')}</div>
        </div>

        {meta.needsDimension && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.textDim, marginBottom: 8, textTransform: 'uppercase' as const, letterSpacing: '.06em' }}>{t('Kırılım')}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const }}>
              {Object.entries(DIMENSION_LABELS).map(([id, label]) => (
                <button key={id} onClick={() => setDimension(id)} style={{ padding: '6px 11px', borderRadius: 999, border: `1.5px solid ${dimension === id ? T.accent : T.border}`, background: dimension === id ? 'rgba(201,168,76,.1)' : 'var(--py-surface)', color: dimension === id ? T.accentDeep : T.textMid, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>{t(label)}</button>
              ))}
            </div>
          </div>
        )}

        {meta.needsMetric && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.textDim, marginBottom: 8, textTransform: 'uppercase' as const, letterSpacing: '.06em' }}>{t('Metrik')}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const }}>
              {Object.keys(METRIC_LABELS).map(m => (
                <button key={m} onClick={() => setMetric(m)} style={{ padding: '6px 11px', borderRadius: 999, border: `1.5px solid ${metric === m ? T.accent : T.border}`, background: metric === m ? 'rgba(201,168,76,.1)' : 'var(--py-surface)', color: metric === m ? T.accentDeep : T.textMid, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>{t(METRIC_LABELS[m])}</button>
              ))}
            </div>
          </div>
        )}

        {meta.multiMetric && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.textDim, marginBottom: 8, textTransform: 'uppercase' as const, letterSpacing: '.06em' }}>{t('Metrikler')}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const }}>
              {Object.keys(METRIC_LABELS).map(m => {
                const active = metricsMulti.includes(m);
                return (
                  <button key={m} onClick={() => toggleMetricMulti(m)} style={{ padding: '6px 11px', borderRadius: 999, border: `1.5px solid ${active ? T.accent : T.border}`, background: active ? 'rgba(201,168,76,.1)' : 'var(--py-surface)', color: active ? T.accentDeep : T.textMid, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>{t(METRIC_LABELS[m])}</button>
                );
              })}
            </div>
            <div style={{ fontSize: 11, color: T.textFaint, marginTop: 6 }}>{t('Kartta gösterilecek metrikleri seç (en az bir tane).')}</div>
          </div>
        )}

        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.textDim, marginBottom: 8, textTransform: 'uppercase' as const, letterSpacing: '.06em' }}>{t('Widget Başlığı')}</div>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder={t('Örn. Toplam Harcama')} style={{ width: '100%', boxSizing: 'border-box' as const, padding: '10px 12px', borderRadius: 9, border: `1px solid ${T.borderMid}`, fontSize: 13, fontFamily: 'inherit' }} />
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
          <Button variant="ghost" onClick={onClose}>{t('Vazgeç')}</Button>
          <Button variant="solid" onClick={save} disabled={!title.trim()}>{initial ? t('Kaydet') : t('Widget Ekle')}</Button>
        </div>
      </div>
    </div>
  );
}
